import { Inject, Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
    HttpResponse,
} from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '../services/store.service';
import { Observable, throwError } from 'rxjs';
import { CookieConsentService } from '../services/cookie-consent-service';
import { APP_BASE_HREF } from '@angular/common';

@Injectable()
export class ApiResponseInterceptor implements HttpInterceptor {
    constructor(
        @Inject(APP_BASE_HREF) private baseHref: string,
        private router: Router,
        private cookieService: CookieConsentService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Clone request with appropriate response type
        request = request.clone({
            responseType:
                request.responseType === 'arraybuffer' ||
                request.responseType === 'blob'
                    ? request.responseType
                    : 'text',
        });

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                let errorObject = error.error;

                // Parse error object if it's a string
                if (typeof errorObject === 'string') {
                    try {
                        errorObject = JSON.parse(error.error);
                    } catch (e) {
                        errorObject = {
                            msg: 'Не поддерживаемый формат ошибки.',
                        };
                    }
                }

                // Handle 401 Unauthorized error
                if (error.status === 401) {
                    // Clear all authentication data
                    this.clearAuthData();

                    // Redirect to login page unless it's a logout request
                    if (!request.url.includes('logout')) {
                        this.router.navigate(['/login']);
                    }
                }

                return throwError(() => errorObject || error);
            }),
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    const contentTypeHeader = event.headers
                        .get('Content-Type')
                        ?.split(';')[0];
                    const { body } = event;

                    // Parse JSON responses
                    if (
                        body &&
                        typeof body === 'string' &&
                        contentTypeHeader === 'application/json'
                    ) {
                        try {
                            const parsedBody = JSON.parse(body);
                            if (typeof parsedBody === 'object') {
                                return event.clone({
                                    body: parsedBody.data || parsedBody,
                                });
                            }
                        } catch (e) {
                            console.error('JSON parsing error', e);
                            return event;
                        }
                    }
                    return event;
                }
                return event;
            })
        );
    }

    /**
     * Clear all authentication data when receiving 401 error
     */
    private clearAuthData(): void {
        try {
            // Clear localStorage
            localStorage.clear();

            // Clear sessionStorage
            sessionStorage.clear();

            // Clear custom store if available
            if (Store && typeof Store.clearAllStore === 'function') {
                Store.clearAllStore();
            }

            // Clear all cookies including specific ones like pa-web_Patient_Local
            this.clearAllCookies();
        } catch (e) {
            console.warn('Error while clearing auth data:', e);
        }
    }

    /**
     * Clear all cookies including those with different paths/domains
     */
  

    private clearAllCookies(): void {
        const cookieNames = [
            'pa-web_Patient_Local',
            'pa-web_Patient',
            'cookieconsent_status',
            'sessionTokenAuthAriadna',
        ];

        // --- 1. Получаем пути из APP_BASE_HREF и текущего URL ---
        const basePathFromHref = this.baseHref?.replace(/\/$/, '') || ''; // Убираем trailing slash
        const currentPath = window.location.pathname;
        const currentBasePath =
            '/' + currentPath.split('/').slice(1, -1).join('/'); // /lk/oracle/master

        // --- 2. Собираем все возможные пути ---
        const paths = new Set<string>(
            [
                '/',
                '/home',
                '/login',
                '/auth',
                basePathFromHref,
                basePathFromHref + '/home',
                basePathFromHref + '/login',
                currentBasePath,
                currentPath,
                currentBasePath + '/home',
                currentBasePath + '/login',
            ].filter(Boolean)
        ); // Убираем пустые

        // --- 3. Домены ---
        const domains = [
            '',
            window.location.hostname,
            `.${window.location.hostname}`,
        ].filter(Boolean);

        // --- 4. Флаг Secure ---
        const isSecure =
            window.location.protocol === 'https:' ? '; Secure' : '';

        // --- 5. Удаляем каждую куку всеми способами ---
        cookieNames.forEach((name) => {
            // Сначала через сервис (он может быть ограничен, но всё равно попробуем)
            try {
                this.cookieService.deleteCookie(name);
            } catch (e) {
                console.warn(
                    `[Cookie] Failed to delete via service: ${name}`,
                    e
                );
            }

            // Пробуем все комбинации path + domain
            paths.forEach((path) => {
                domains.forEach((domain) => {
                    try {
                        const domainPart = domain ? `; domain=${domain}` : '';
                        const pathPart = path ? `; path=${path}` : '; path=/';
                        const expires =
                            '; expires=Thu, 01 Jan 1970 00:00:01 GMT';
                        const sameSite = '; SameSite=Lax';

                        // Удаляем с полным набором
                        document.cookie = `${name}=${expires}${pathPart}${domainPart}${sameSite}${isSecure}`;
                    } catch (e) {
                        console.warn(
                            `[Cookie] Failed to delete ${name} at path=${path} domain=${domain}`,
                            e
                        );
                    }
                });
            });

            // Финальные попытки — на случай, если не сработало
            try {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/home`;
                if (basePathFromHref) {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${basePathFromHref}`;
                }
            } catch (e) {
                console.warn(`[Cookie] Final fallback failed for ${name}`, e);
            }
        });

        // --- 6. Используем deleteAll с APP_BASE_HREF ---
        try {
            this.cookieService.deleteAll();
            if (basePathFromHref) {
                this.cookieService.deleteAll(basePathFromHref);
            }
        } catch (e) {
            console.warn('[Cookie] deleteAll failed', e);
        }

        // --- 7. Ручная очистка всех кук (на всякий случай) ---
        try {
            document.cookie.split(';').forEach((rawCookie) => {
                const name = rawCookie.split('=')[0].trim();
                if (name) {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
                    if (basePathFromHref) {
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${basePathFromHref}`;
                    }
                }
            });
        } catch (e) {
            console.warn('[Cookie] Manual cleanup failed', e);
        }
    }
}
